import React from 'react';

const XSearchList = ({ 
  searches, 
  loading, 
  onEdit, 
  onDelete, 
  onRun, 
  onViewResults,
  onManageSchedules, 
  runningSearchId,
  sortField,
  sortDirection,
  onSort
}) => {
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
    }
    return sortDirection === 'asc' 
      ? <i className="bi bi-arrow-up ms-1"></i>
      : <i className="bi bi-arrow-down ms-1"></i>;
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      onClick={() => onSort(field)} 
      style={{ 
        cursor: 'pointer', 
        userSelect: 'none',
        transition: 'background-color 0.2s',
        whiteSpace: 'nowrap'
      }}
      className="sortable-header"
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
    >
      {children}
      {renderSortIcon(field)}
    </th>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading searches...</p>
      </div>
    );
  }

  if (searches.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <i className="bi bi-search display-1 text-muted"></i>
          <h5 className="mt-3">No searches configured</h5>
          <p className="text-muted">
            Create your first X search to start monitoring posts
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Search Configurations ({searches.length})</h5>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <SortableHeader field="name">Name</SortableHeader>
                <th>Description</th>
                <th>Query</th>
                <th>Time Constraint</th>
                <th>Schedules</th>
                <SortableHeader field="max_results">Results</SortableHeader>
                <SortableHeader field="status">Status</SortableHeader>
                <SortableHeader field="last_run_at">Last Run</SortableHeader>
                <SortableHeader field="created_at">Created</SortableHeader>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {searches.map((search) => (
                <tr key={search.id}>
                  <td>
                    <strong>{search.name}</strong>
                  </td>
                  <td>
                    <small className="text-muted">
                      {search.description || <em>No description</em>}
                    </small>
                  </td>
                  <td>
                    <code className="small">{search.query}</code>
                  </td>
                  <td>
                    <small className="text-muted">
                      {search.time_constraint_value && search.time_constraint_unit ? (
                        <span className="badge bg-info text-dark">
                          {search.time_constraint_value} {search.time_constraint_value === 1 
                            ? search.time_constraint_unit.slice(0, -1) 
                            : search.time_constraint_unit}
                        </span>
                      ) : (
                        <span className="text-muted">None</span>
                      )}
                    </small>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => onManageSchedules(search)}
                      title="Manage schedules"
                    >
                      <i className="bi bi-calendar-event me-1"></i>
                      {search.schedules && search.schedules.length > 0 ? (
                        <span className="badge bg-primary ms-1">{search.schedules.length}</span>
                      ) : (
                        'Add'
                      )}
                    </button>
                  </td>
                  <td>{search.max_results}</td>
                  <td>
                    {search.is_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>
                  <td className="small text-muted">
                    {search.last_run_at
                      ? new Date(search.last_run_at).toLocaleString()
                      : <span className="text-muted">Never run</span>}
                  </td>
                  <td className="small text-muted">
                    {search.created_at
                      ? new Date(search.created_at).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => onRun(search.id)}
                        disabled={!search.is_active || runningSearchId === search.id}
                        title="Run search"
                      >
                        {runningSearchId === search.id ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-play-fill"></i>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-info"
                        onClick={() => onViewResults(search.id)}
                        title="View recent results"
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => onEdit(search)}
                        title="Edit search"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => onDelete(search.id)}
                        title="Delete search"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default XSearchList;
